import React from 'react';
import styled from '@emotion/styled';
import BgSpImg from 'ui/images/top/topLead_bg_sp.svg';
import BgPcImg from 'ui/images/top/topLead_bg_pc.svg';
import TopWebp from 'ui/images/top/topLead_img@2x.webp';
import TopImg from 'ui/images/top/topLead_img@2x.png';
import { Only } from 'ui/components/elements/Only/Only';
import { mqMin } from 'utils/responsive';

const Container = styled.section`
    background-image: url(${BgSpImg});
    background-repeat: no-repeat;
    background-size: 100% auto;
    background-position: center center;
    padding: 61px 0 56px 0;
    color: var(--dark-blue);

    h2 {
        position: relative;
        text-align: center;
        line-height: 1.5em;
        letter-spacing: 0.05em;
        font-size: 1.5rem; /*24px*/
        margin-bottom: 23px;
    }

    ${mqMin('NARROW')} {
        background-image: url(${BgPcImg});
        padding: 80px 0 77px 0;

        h2 {
            font-size: 2rem; /*32px*/
            line-height: 1.8em;
        }
    }
`;

const H2Company = styled.span`
    font-size: 1.875rem; /*30px*/

    ${mqMin('NARROW')} {
        font-size: 2.3125rem; /*37px*/
    }
`;

const Strong = styled.strong`
    position: relative;
    font-size: 1.75rem;

    ${mqMin('NARROW')} {
        font-size: 2.3125rem;
    }
`;

const StrongWithAfter = styled(Strong)`
    &::after {
        position: absolute;
        top: -20px;
        left: 50%;
        content: '・';
        transform: translateX(-50%);
        font-size: 30px;

        ${mqMin('NARROW')} {
            top: -30px;
            font-size: 35px;
        }
    }
`;

const OrangeColorStrong = styled(Strong)`
    color: var(--orange);
`;

const OrangeSpan = styled.span`
    color: var(--orange);
`;

const Text = styled.p`
    text-align: center;
    margin-bottom: 23px;

    ${mqMin('NARROW')} {
        margin-bottom: 40px;
    }
`;

const CirclesContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 20px;
    margin: 30px auto 30px auto;
    width: 88.37209302325581%;

    ${mqMin('SP')} {
        flex-direction: row;
        justify-content: center;
    }

    ${mqMin('NARROW')} {
        gap: 40px;
        margin: 40px auto 40px auto;
        max-width: 900px;
    }
`;

const Circle = styled.div<{ bgColor: string; textColor?: string }>`
    width: 10rem;
    height: 10rem;
    border-radius: 50%;
    background-color: ${(props) => props.bgColor};
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 1.25rem;
    text-align: center;
    color: ${(props) => props.textColor || 'var(--dark-blue)'};
    border: 2px solid var(--dark-blue);

    ${mqMin('NARROW')} {
        width: 15rem;
        height: 15rem;
        padding: 1.875rem;
    }
`;

const CircleTitle = styled.div`
    font-size: 0.75rem;
    font-weight: bold;
    margin-bottom: 5px;
    line-height: 1.4em;

    ${mqMin('NARROW')} {
        font-size: 1.2rem;
    }
`;

const CircleDivider = styled.div<{ dividerColor?: string }>`
    width: 30px;
    height: 2px;
    background-color: ${(props) => props.dividerColor || 'var(--dark-blue)'};
    margin: 8px 0;

    ${mqMin('NARROW')} {
        margin: 12px 0;
    }
`;

const CircleText = styled.div`
    font-size: 0.65rem;
    line-height: 1.6em;
    letter-spacing: 0.05em;

    ${mqMin('NARROW')} {
        font-size: 0.8rem;
    }
`;

const Img = styled.img`
    width: 348px;
    margin: 0 auto;
    display: block;

    ${mqMin('NARROW')} {
        width: 770px;
    }
`;

export const LeadSection: React.FC = () => {
    return (
        <Container>
            <h2 className="fadein fadein_y">
                <H2Company>MAny</H2Company>なら<StrongWithAfter>完</StrongWithAfter>
                <StrongWithAfter>全</StrongWithAfter>
                <StrongWithAfter>無</StrongWithAfter>
                <StrongWithAfter>料</StrongWithAfter>で<br />
                <OrangeColorStrong>M&Aの知見を持つ専門家</OrangeColorStrong>
                <OrangeSpan>から</OrangeSpan>
                <Only sp>
                    <br />
                </Only>
                <OrangeColorStrong>情報収集＆比較検討</OrangeColorStrong>ができます
            </h2>
            <Text className="fadein fadein_y">
                中小企業の経営者が、事業承継に関する不安や悩み、
                <Only sp>
                    <br />
                </Only>
                知りたいことを 最大5社の専門家へ無料で相談が可能です。
            </Text>
            <CirclesContainer className="fadein fadein_y">
                <Circle bgColor="#1796D3" textColor="#fff">
                    <CircleTitle>中小企業の経営者</CircleTitle>
                    <CircleDivider dividerColor="#fff" />
                    <CircleText>
                        事業承継に関するあなたが欲しい情報を
                        <br />
                        収集・比較検討ができる
                    </CircleText>
                </Circle>
                <Circle bgColor="#e7e7e7">
                    <CircleTitle>事業承継専門家</CircleTitle>
                    <CircleDivider />
                    <CircleText>
                        事業承継に悩む中小企業経営者に対して
                        <br />
                        直接アプローチが可能
                    </CircleText>
                </Circle>
                <Circle bgColor="#ee793f" textColor="#fff">
                    <CircleTitle>
                        事業譲受検討中
                        <br />
                        の経営者
                    </CircleTitle>
                    <CircleDivider dividerColor="#fff" />
                    <CircleText>
                        希望条件にマッチした案件を持つ
                        <br />
                        事業承継専門家を探せる
                    </CircleText>
                </Circle>
            </CirclesContainer>
            <picture>
                <source type="image/webp" srcSet={TopWebp} />
                <Img src={TopImg} alt="複数の会社と面談イメージ" />
            </picture>
        </Container>
    );
};
